import React, { Fragment, useRef, useState, useEffect } from "react";
import COLORS from "../../../assets/Colors";
import CustomTable from "../../common/CustomTable/CustomTable";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import queryString from "query-string";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import styled from "styled-components";
import { navigate } from "@reach/router";
import moment from "moment";
import OnsitePaymentFlow from "../OnsitePayment";
import AdminCancelModal from "../modals/AdminCancelModal";
import BookingDetail from "./BookingDetail";
import BookingService from "../../../services/BookingService";
import useLoader from "../../../hooks/useLoader";
import useRazorPay from "../../../hooks/useRazorPay";
import { BookingInvoiceSection } from "./BookingInvoiceSection";
import { downloadInvoice } from "../data/mockBookings";
import AdminCancel from "./AdminCancel";
import theme from "../../../assets/theme";
import CancellationAttachmentPreview from "../modals/CancellationAttachmentPreview";
import VisibilityIcon from "@material-ui/icons/Visibility";

const initFilters = [
  {
    label: "All",
    key: "all",
    value: 0,
  },
  {
    label: "Active",
    key: "active",
    value: 0,
  },
  {
    label: "Completed",
    key: "completed",
    value: 0,
  },
  {
    label: "Cancelled",
    key: "cancelled",
    value: 0,
  },
  {
    label: "Reserved",
    key: "reserved",
    value: 0,
  },
];

const filterByStatus = (selectedFilter) => {
  let key = "";
  switch (selectedFilter) {
    case "All":
      key = "all";
      break;
    case "Active":
      key = "active";
      break;
    case "Completed":
      key = "completed";
      break;
    case "Cancelled":
      key = "cancelled";
      break;
    case "Reserved":
      key = "reserved";
      break;
    default:
      break;
  }
  return key;
};

async function getBookingsList(query = "") {
  try {
    const response = await BookingService.fetchBookings(query);
    return { ...response };
  } catch (error) {
    throw error;
  }
}

const BookingsPage = () => {
  const breadcrumbs = ["Booking Dashboard", "Bookings"];
  const [state, setState] = useState({});
  const MTRef = useRef();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [filters, setFilters] = useState(initFilters);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [openBooking, setOpenBooking] = useState(null);
  const [onsiteOpen, setOnsiteOpen] = useState(false);
  const [onsiteBooking, setOnsiteBooking] = useState(null);
  const [adminCancelBooking, setAdminCancelBooking] = useState(null);
  const openRazorPay = useRazorPay();
  const [adminCancelOpen, setAdminCancelOpen] = useState({ state: false });
  // Preview state for cancellation supporting documents (All tab only).
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const handleCloseBooking = () => setOpenBooking(null);

  const handleRecordOnsite = (b) => {
    setOnsiteBooking(b || null);
    setOnsiteOpen(true);
  };
  const handleCloseOnsite = () => {
    setOnsiteOpen(false);
    setOnsiteBooking(null);
  };

  const handleAdminCancel = (b) => setAdminCancelBooking(b);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "booking_dashboard":
        navigate("/dashboard/case-manager/booking-dashboard");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (selectedFilter && MTRef?.current?.onQueryChange) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (selectedFilter !== "Reserved") return;

    const timer = setTimeout(
      () => {
        if (MTRef?.current?.onQueryChange) {
          MTRef.current.onQueryChange(MTRef?.current?.state?.query);
        }
      },
      10 * 60 * 1000,
    );

    return () => clearTimeout(timer);
  }, [selectedFilter]);

  function setStats(stats) {
    const mapped = initFilters.map((filter) => {
      let returnData = {
        label: filter.label,
      };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  const isCancelled = selectedFilter === "Cancelled";

  const columns = [
    {
      field: "booking_reference",
      title: "REF",
      sorting: true,
      headerStyle: { width: "10%" },
      cellStyle: { width: "10%" },
      render: (rowData) =>
        selectedFilter === "Active" ? (
          <HyperLink
            onClick={() => {
              setOpenBooking(rowData);
            }}
          >
            {rowData.booking_reference || "-"}
          </HyperLink>
        ) : (
          rowData.booking_reference || "-"
        ),
    },
    {
      field: "hearing_date",
      title: "DATE & TIME",
      sorting: true,
      headerStyle: { width: "15%" },
      cellStyle: { width: "15%" },
      render: (rowData) => {
        const dateFormatted = moment(rowData.hearing_date).format("DD MMM");

        const dayInfo = moment(rowData.hearing_date).isSame(moment(), "day")
          ? "Today"
          : moment(rowData.hearing_date).isSame(moment().add(1, "day"), "day")
            ? "Tomorrow"
            : "";

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{dateFormatted}</div>
            <div
              style={{ fontSize: "13px", color: "#949ab0", marginTop: "2px" }}
            >
              {rowData.start_time} – {rowData.end_time}
              {dayInfo && ` • ${dayInfo}`}
            </div>
          </div>
        );
      },
    },
    {
      field: "hearing_room",
      title: "ROOM",
      sorting: false,
      headerStyle: { width: isCancelled ? "11%" : "12%" },
      cellStyle: { width: isCancelled ? "11%" : "12%" },
      render: (rowData) => (
        <div>
          <div>{rowData.hearing_room || "—"}</div>
          <div style={{ fontSize: "13px", color: "#949ab0", marginTop: "2px" }}>
            {rowData.session_duration || "-"}
          </div>
        </div>
      ),
    },
    {
      field: "parties",
      title: "PARTIES",
      sorting: false,
      headerStyle: { width: isCancelled ? "20%" : "23%" },
      cellStyle: { width: isCancelled ? "20%" : "23%" },
      render: (rowData) => {
        const claimant = rowData.claimants_list?.[0]?.Name || "-";
        const respondent = rowData.respondents_list?.[0]?.Name || "-";

        return (
          <div style={{ fontSize: "13.5px" }}>
            <div>{claimant}</div>
            <div style={{ color: "#949ab0" }}>vs. {respondent}</div>
          </div>
        );
      },
    },
    {
      field: "bookedBy",
      title: "BOOKED BY",
      sorting: false,
      headerStyle: { width: isCancelled ? "13%" : "14%" },
      cellStyle: { width: isCancelled ? "13%" : "14%" },
      render: (rowData) => (
        <div>
          <div>{rowData.owner_details?.name || "-"}</div>
          <div style={{ fontSize: "13px", color: "#949ab0" }}>
            {rowData.owner_details?.phone || "-"}
          </div>
        </div>
      ),
    },
    {
      field: "total_amount",
      title: "TOTAL",
      sorting: false,
      headerStyle: { width: isCancelled ? "11%" : "12%" },
      cellStyle: { width: isCancelled ? "11%" : "12%" },
      render: (rowData) => {
        const amount = rowData.grand_total || rowData.sub_total || 0;
        const isPaid = rowData.payment_status?.toLowerCase() === "paid";

        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: "16px" }}>
              ₹{amount.toLocaleString("en-IN")}
            </div>
            <div
              style={{
                background: isPaid ? "#E8F5E9" : "#FFF3E0",
                color: isPaid ? "#00C853" : "#FF9800",
                fontSize: "12px",
                fontWeight: 700,
                padding: "2px 10px",
                borderRadius: "12px",
                display: "inline-block",
                marginTop: "4px",
                textTransform: "uppercase",
              }}
            >
              {rowData.payment_status || "PENDING"}
            </div>
          </div>
        );
      },
    },
    {
      field: "booking_status",
      title: "BOOKING STATUS",
      sorting: false,
      headerStyle: {
        width: isCancelled ? "12%" : "14%",
        paddingLeft: "8px",
      },
      cellStyle: { width: isCancelled ? "12%" : "14%" },
      render: (rowData) => {
        const status = rowData.booking_status?.toLowerCase();

        let bg = "#E3F2FD";
        let color = "#1976D2";

        if (status === "confirmed") {
          bg = "#E8F5E9";
          color = "#2E7D32";
        } else if (status === "reserved") {
          bg = "#FFF3E0";
          color = "#F57C00";
        } else if (status === "cancelled") {
          bg = "#FFEBEE";
          color = "#C62828";
        }

        return (
          <div style={{ display: "inline-flex", flexDirection: "row", alignItems: "center", gap: 6 }}>
            <div
              style={{
                background: bg,
                color,
                fontSize: "13px",
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: "12px",
                display: "inline-block",
                textTransform: "uppercase",
              }}
            >
              {rowData.booking_status || "-"}
            </div>
            {/* Eye icon: All tab, cancelled rows with a supporting document only */}
            {selectedFilter === "All" &&
              status === "cancelled" &&
              rowData.bookingRoomInvoice?.some((invoice) => invoice.cancelFileurl) && (
                <EyeIconBtn
                  title="View supporting document"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentPreview(rowData);
                  }}
                >
                  <VisibilityIcon style={{ fontSize: 20 }} />
                </EyeIconBtn>
              )}
          </div>
        );
      },
    },
    {
      field: "",
      title: "Action",
      sorting: false,
      hidden: !isCancelled,
      headerStyle: {
        width: "8%",
        paddingLeft: "8px",
      },
      cellStyle: {
        width: "8%",
      },
      render: (rowData) => {
        const hasDoc = rowData.bookingRoomInvoice?.some(
          (invoice) => invoice.cancelFileurl
        );

        return rowData?.refund_status === "refundInProgress" ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <HyperLink
              style={{
                color: COLORS.LOGOUT_RED,
                fontFamily: theme.fonts.primaryFontSemiBold,
              }}
              onClick={() =>
                setAdminCancelOpen({ state: true, booking: rowData })
              }
            >
              Refund
            </HyperLink>
            {hasDoc && (
              <EyeIconBtn
                title="View supporting document"
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentPreview(rowData);
                }}
              >
                <VisibilityIcon style={{ fontSize: 20 }} />
              </EyeIconBtn>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>-</span>
            {hasDoc && (
              <EyeIconBtn
                title="View supporting document"
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentPreview(rowData);
                }}
              >
                <VisibilityIcon style={{ fontSize: 20 }} />
              </EyeIconBtn>
            )}
          </div>
        );
      },
    },
  ];

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        perPage: 10,
        status: filterByStatus(selectedFilter),
        room: "",
      };

      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }

      if (query?.search) {
        params.search = query.search;
      }

      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }

      getBookingsList(stringParams)
        .then((result) => {
          resolve({
            data: result.data?.data,
            page: result?.data?.page - 1,
            total: result?.data?.total,
          });
          setState(result?.data);
          setStats(result.counts);
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          setState({ data: [], page: 0, total: 0 });
          resolve({ data: [], page: 0, total: 0 });
        });
    });

  async function capturePayment(rzp_res) {
    document.body.style.overflow = null;
    try {
      setLoader({ state: true, message: "Capturing Payment..." });
      const capture_res = await BookingService.capturePayment(rzp_res);
      if (capture_res) {
        enqueueSnackbar(capture_res.message, { variant: "success" });
        downloadInvoice(capture_res?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function onFormSubmit(data) {
    try {
      setLoader({ state: true, message: "Adding onsite service..." });
      const response = await BookingService.addOnsiteService(data);
      if (data?.modeOfPayment === "razorpayLink") {
        openRazorPay({
          order_id: response.razorpayOrderId,
          handler: capturePayment,
        });
        return;
      }
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
        downloadInvoice(response?.invoice_url);
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setOnsiteOpen(false);
      setOpenBooking(false);
      setAdminCancelOpen({ state: false });
    }
  }

  async function handleCancelConfirm(data) {
    try {
      setAdminCancelOpen({ state: false });
      setLoader({ state: true, message: "cancel booking..." });
      const response = await BookingService.cancelBooking(data);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setAdminCancelBooking(null);
      setOpenBooking(null);
      handleCloseBooking();
    }
  }

  const detailPanel = [
    {
      tooltip: "View detailed split up...",
      render: (rowData) => {
        return (
          <BookingInvoiceSection
            bookingRoomInvoice={rowData?.bookingRoomInvoice}
            bookingRoomRefunds={rowData?.bookingRoomRefunds}
          />
        );
      },
    },
  ];

  return (
    <Fragment>
      <ActionBar breadcrumbs={breadcrumbs} {...{ onBreadcrumbClick }} />
      <Padding>
        <CustomTable
          hidePagination={state.lastPage === 1}
          pageSize={state?.data?.length || 10}
          pluralTitle="Bookings"
          singularTitle={false}
          hideFilterBar={false}
          placeholderText="Search Bookings..."
          state={state}
          detailPanel={detailPanel}
          {...{
            columns,
            data,
            filters,
            selectedFilter,
            setSelectedFilter,
            MTRef,
          }}
        />
      </Padding>

      <BookingDetail
        booking={openBooking}
        onClose={handleCloseBooking}
        onRecordOnsite={handleRecordOnsite}
        onAdminCancel={handleAdminCancel}
        {...{ MTRef }}
      />

      {onsiteOpen && (
        <OnsitePaymentFlow
          bookings={onsiteBooking}
          preSelectedBooking={onsiteBooking}
          onClose={handleCloseOnsite}
          onComplete={(data) => {
            onFormSubmit(data);
          }}
        />
      )}

      {/* Admin cancel modal */}
      {adminCancelBooking && (
        <AdminCancelModal
          booking={adminCancelBooking}
          onClose={() => setAdminCancelBooking(null)}
          onConfirm={(meta) => handleCancelConfirm(meta)}
        />
      )}
      {adminCancelOpen?.state && (
        <AdminCancel
          booking={adminCancelOpen?.booking}
          onClose={() => setAdminCancelOpen({ state: false })}
          onConfirm={(meta) => handleCancelConfirm(meta)}
        />
      )}
      {/* Cancellation attachment preview — All and Cancelled tabs */}
      {attachmentPreview && (
        <CancellationAttachmentPreview
          booking={attachmentPreview}
          onClose={() => setAttachmentPreview(null)}
        />
      )}
    </Fragment>
  );
};

export default BookingsPage;

const Padding = styled.div`
  padding-top: 30px;
  padding-bottom: 50px;
  padding-left: 35px;
  padding-right: 53px;
`;

const HyperLink = styled.span`
  cursor: pointer;
  color: ${COLORS.BTN_GREEN};
  text-decoration: underline;
`;


const EyeIconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${COLORS.BTN_GREEN};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  &:hover {
    background: rgba(0, 131, 140, 0.1);
  }
`;
